import { ID, UsageStatisticsEntry, User } from '@griply/types';
import { recordPmfCompletion, recordPmfSession } from '@griply/utils';
import { useCallback } from 'react';

// The user-doc security rule rejects a second update within 100ms of the
// previous one (isNotRateLimited). PMF writes the user doc on app-open and on
// every completion, so bursts collide. We accumulate into a module-level cache
// (also the source of truth for rapid successive completions) and flush a
// single coalesced write on a trailing debounce, keeping writes well past the
// rate-limit window.
export const WRITE_DEBOUNCE_MS = 1000;

let cachedEntries: UsageStatisticsEntry[] | null = null;
let cachedUid: ID | null = null;
let flushTimer: ReturnType<typeof setTimeout> | null = null;

export type UpdatePmfUsageStatistics = (fields: {
  usageStatistics: UsageStatisticsEntry[];
}) => void;

const scheduleFlush = (write: UpdatePmfUsageStatistics) => {
  if (flushTimer) {
    clearTimeout(flushTimer);
  }
  flushTimer = setTimeout(() => {
    flushTimer = null;
    if (cachedEntries) {
      write({ usageStatistics: cachedEntries });
    }
  }, WRITE_DEBOUNCE_MS);
};

const resolveBase = (user: User): UsageStatisticsEntry[] => {
  if (cachedUid !== user.uid || cachedEntries === null) {
    cachedUid = user.uid;
    cachedEntries = user.usageStatistics ?? [];
  }
  return cachedEntries;
};

export const recordPmfSessionActivity = (
  user: User | null | undefined,
  write: UpdatePmfUsageStatistics,
) => {
  if (!user || user.pmfSurvey?.surveyCompleted) {
    return;
  }
  const { entries, changed } = recordPmfSession(resolveBase(user), new Date());
  cachedEntries = entries;
  if (changed) {
    scheduleFlush(write);
  }
};

export const recordPmfCompletionActivity = (
  user: User | null | undefined,
  write: UpdatePmfUsageStatistics,
) => {
  if (!user || user.pmfSurvey?.surveyCompleted) {
    return;
  }
  const { entries } = recordPmfCompletion(resolveBase(user), new Date());
  cachedEntries = entries;
  scheduleFlush(write);
};

export const useRecordPmfActivity = (
  user: User | null | undefined,
  updateUsageStatistics: UpdatePmfUsageStatistics,
) => {
  const recordSessionActivity = useCallback(
    () => recordPmfSessionActivity(user, updateUsageStatistics),
    [user, updateUsageStatistics],
  );

  const recordCompletion = useCallback(
    () => recordPmfCompletionActivity(user, updateUsageStatistics),
    [user, updateUsageStatistics],
  );

  return { recordSessionActivity, recordCompletion };
};
